import React, { useState, useEffect } from 'react';
import {
  Button,
  Tag,
  Space,
  Modal,
  Row,
  Col,
  Card,
  Statistic,
  message,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useArticles } from '../../../hooks/useContent';
import { DataTable } from '../../../components';
import { ArticleCategory } from '../../../services/content.service';
import { CommonState } from '@erpgold/shared';

const statusColors: Record<number, string> = {
  [CommonState.ENABLED]: 'success',
  [CommonState.DISABLED]: 'error',
};

export const ArticleList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);

  const {
    articles,
    loading,
    meta,
    filters,
    setFilters,
    refetch,
    deleteArticles,
  } = useArticles();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { contentService } = await import('../../../services/content.service');
        const data = await contentService.articleCategories.getAll();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, q: value, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string | number | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteArticles([id]);
      message.success('Article deleted successfully');
    } catch (error) {
      message.error('Failed to delete article');
    }
  };

  const handleDeleteMany = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select articles to delete');
      return;
    }
    Modal.confirm({
      title: 'Delete Articles',
      content: `Are you sure you want to delete ${selectedRowKeys.length} article(s)?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteArticles(selectedRowKeys as string[]);
          setSelectedRowKeys([]);
          message.success('Articles deleted successfully');
        } catch (error) {
          message.error('Failed to delete articles');
        }
      },
    });
  };

  const flattenCategories = (cats: ArticleCategory[], prefix = ''): { label: string; value: string }[] => {
    const result: { label: string; value: string }[] = [];
    for (const cat of cats) {
      result.push({ label: `${prefix}${cat.name}`, value: cat.category_id || cat._id });
      if (cat.children && cat.children.length > 0) {
        result.push(...flattenCategories(cat.children, `${prefix}-- `));
      }
    }
    return result;
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <span>{text}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.slug}</span>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (cat) => cat?.name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => (
        <Tag color={statusColors[state] || 'default'}>
          {state === CommonState.ENABLED ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: 150,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={filters.state}
          onChange={(value) => handleFilterChange('state', value)}
        >
          <Select.Option value={CommonState.ENABLED}>Enabled</Select.Option>
          <Select.Option value={CommonState.DISABLED}>Disabled</Select.Option>
        </Select>
      </Col>
      <Col span={8}>
        <Select
          placeholder="Filter by category"
          allowClear
          style={{ width: '100%' }}
          value={filters.category_id}
          onChange={(value) => handleFilterChange('category_id', value)}
        >
          {flattenCategories(categories).map((cat) => (
            <Select.Option key={cat.value} value={cat.value}>{cat.label}</Select.Option>
          ))}
        </Select>
      </Col>
    </Row>
  );

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total Articles" value={meta.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active"
              value={articles.filter((a: any) => a.state === CommonState.ENABLED).length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive"
              value={articles.filter((a: any) => a.state === CommonState.DISABLED).length}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Articles"
        data={articles}
        columns={columns}
        loading={loading}
        rowKey="article_id"
        searchable
        searchPlaceholder="Search by title..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: (page, pageSize) => setFilters((prev) => ({ ...prev, page, limit: pageSize })),
        }}
        selectionType="checkbox"
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        extraActions={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDeleteMany}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/content/articles/add')}>
              Add Article
            </Button>
          </Space>
        }
        actionColumn={{
          width: 120,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'edit',
              label: 'Edit',
              icon: <EditOutlined />,
              onClick: () => navigate(`/content/articles/${record._id}/edit`),
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: 'Delete Article',
                  content: `Are you sure you want to delete "${record.title}"?`,
                  onOk: () => handleDelete(record._id),
                });
              },
            },
          ],
        }}
      />
    </div>
  );
};

export default ArticleList;
